import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

/**
 * Context for tracking global audio playback state across the application.
 *
 * This allows any component to:
 * 1. Register when audio starts playing
 * 2. Unregister when audio stops
 * 3. Check if ANY audio is currently playing
 *
 * Primary use case: Automatically disable microphone during audio playback
 */

interface AudioPlaybackContextValue {
  /** True if any audio player is currently playing */
  isPlaying: boolean;

  /** Register an audio player when it starts playing */
  registerPlayer: (id: string) => void;

  /** Unregister an audio player when it stops */
  unregisterPlayer: (id: string) => void;

  /** Get set of all currently playing audio player IDs (for debugging) */
  getActivePlayers: () => Set<string>;
}

const AudioPlaybackContext = createContext<AudioPlaybackContextValue | undefined>(undefined);

export function AudioPlaybackProvider({ children }: { children: ReactNode }) {
  // Track all active audio players by ID
  const [activePlayers, setActivePlayers] = useState<Set<string>>(new Set());

  const registerPlayer = useCallback((id: string) => {
    setActivePlayers((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregisterPlayer = useCallback((id: string) => {
    setActivePlayers((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const getActivePlayers = useCallback(() => {
    return new Set(activePlayers);
  }, [activePlayers]);

  const isPlaying = activePlayers.size > 0;

  const value: AudioPlaybackContextValue = {
    isPlaying,
    registerPlayer,
    unregisterPlayer,
    getActivePlayers,
  };

  return (
    <AudioPlaybackContext.Provider value={value}>
      {children}
    </AudioPlaybackContext.Provider>
  );
}

/**
 * Hook to access audio playback context.
 *
 * @example
 * ```tsx
 * // In an audio player component
 * const { registerPlayer, unregisterPlayer } = useAudioPlayback();
 *
 * const handlePlay = () => {
 *   registerPlayer('audio-123');
 *   // ... start playing
 * };
 *
 * const handleEnded = () => {
 *   unregisterPlayer('audio-123');
 * };
 * ```
 *
 * @example
 * ```tsx
 * // In microphone component
 * const { isPlaying } = useAudioPlayback();
 *
 * useEffect(() => {
 *   if (isPlaying) {
 *     // Disable microphone
 *   } else {
 *     // Enable microphone
 *   }
 * }, [isPlaying]);
 * ```
 */
export function useAudioPlayback() {
  const context = useContext(AudioPlaybackContext);

  if (context === undefined) {
    throw new Error("useAudioPlayback must be used within AudioPlaybackProvider");
  }

  return context;
}
